import { NgRedux, select } from '@ptl/angular-redux-store';
import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { StateStructure } from '@ptl/state';
import { Observable, Subscription } from 'rxjs';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { ContentStorageService } from '@ptl/global-components';
import { PlansStateStructure } from '../configs/interfaces/plansStateStructure.interface';
import { PRODUCT_GROUPS, PRODUCT_GROUP_NAMES } from '../enums/product-groups.enum';
import { PlanAttributes, SummaryView } from '../interfaces/plan-attributes.interface';
import { Product } from '../interfaces/product.interface';
import { PlanUtilityService } from '../plan-summary-list/plan-utility.service';
import { PLAN_FILTER_REDUCER, resetFilters, toggleFilterValue } from './configs/reducers/plan-filter.reducer';
import { ActiveFilterMap, PlanFilterItem, PlanFilterStateStructure } from './configs/reducers/interfaces/plan-filter.state.interface';
import { RetrievePlansService } from '../plan-summary-list/retrieve-plans.service';
import { GlobalShadowStyleDirective } from '@ptl/global-components';

enum SORTING_ALGORITHMS {
  METAL_LEVEL = 'metalLevel',
  ALPHA_NUMERIC = 'alphaNumeric'
}

//enov8 commit

@Component({
    selector: 'app-plan-filter',
    templateUrl: './plan-filter.component.html',
    styleUrls: ['./plan-filter.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    standalone: false
})
export class PlanFilterComponent extends GlobalShadowStyleDirective implements OnInit, OnDestroy, AfterViewInit {
  // @Input() filterHeaderContentFirst: string;
  // @Input() filterHeaderContentSecond: string;
  // @Input() content;
  // : string = "[{\"filterField\":\"METAL_LEVEL\",\"sortingAlgorithm\":\"metalLevel\",\"filterHeader\":\"Metal Level Filter\"}]";

  products: Map<PRODUCT_GROUP_NAMES, Product<PlanAttributes, SummaryView>[]>;

  plansRetrievedSubscription: Subscription;

  @select(['componentStates', 'Plans', 'activeProductGroup'])
  activeProductGroup$: Observable<PRODUCT_GROUPS>;
  activeProductGroupChanged: Subscription;

  @select(['componentStates', 'Plans', 'plansByPlanType'])
  productsAvailable$: Observable<Map<PRODUCT_GROUP_NAMES, Product<PlanAttributes, SummaryView>[]>>;
  productsAvailableChanged: Subscription;

  contentStorageKey = 'planFilterComponentContent';
  filterContent: Map<string, string> = new Map();
  contentStorageSubscription: Subscription;

  renderableMap: Map<string, { filterHeader: string, values: PlanFilterItem[] }> = new Map();

  showFilters = false;

  @Output() filtersChanged: EventEmitter<null> = new EventEmitter();

  constructor(
    private ngRedux: NgRedux<StateStructure>,
    private planUtilityService: PlanUtilityService,
    private reducerUtilityService: ReducerUtilityService,
    private contentStorageService: ContentStorageService,
    private retrievePlansService: RetrievePlansService
  ) { 
    super()
  }

  ngOnInit(): void {
    /**
     * RESET STATE - When plans are returned from service, no other time
     */
    this.plansRetrievedSubscription = this.retrievePlansService.plansRetrieved$.subscribe(() => {
      this.ngRedux.dispatch(resetFilters());
      this._getPlansResetFilters();
    });

    /** Couldn't get combineLatest to work with store.select - so the below is the equivalent - but exploded */
    this.contentStorageSubscription = this.contentStorageService.contentChanged$.subscribe(() => {
      const planState = (this.reducerUtilityService.getComponentStates()['Plans'] as PlansStateStructure);
      const content = this.contentStorageService.getContentMap(this.contentStorageKey);
      if (planState.activeProductGroup && content?.size > 0) {
        this.filterContent = content;
        this._getPlansResetFilters();
      }
    });
    this.activeProductGroupChanged = this.activeProductGroup$.subscribe(() => {
      const planState = (this.reducerUtilityService.getComponentStates()['Plans'] as PlansStateStructure);
      const content = this.contentStorageService.getContentMap(this.contentStorageKey);
      if (planState.activeProductGroup && content?.size > 0) {
        this.filterContent = content;
        this._getPlansResetFilters();
      }
    });
    this.productsAvailableChanged = this.productsAvailable$.subscribe((products) => {
      const planState = (this.reducerUtilityService.getComponentStates()['Plans'] as PlansStateStructure);
      const content = this.contentStorageService.getContentMap(this.contentStorageKey);
      if (planState.activeProductGroup && content?.size > 0) {
        this.filterContent = content;
        this._getPlansResetFilters();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.plansRetrievedSubscription) {
      this.plansRetrievedSubscription.unsubscribe();
    }
    if (this.contentStorageSubscription) {
      this.contentStorageSubscription.unsubscribe();
    }
    if (this.activeProductGroupChanged) {
      this.activeProductGroupChanged.unsubscribe();
    }
    if (this.contentStorageSubscription) {
      this.contentStorageSubscription.unsubscribe();
    }
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }

  private setProductsPlanMap(): Map<PRODUCT_GROUP_NAMES, Product<PlanAttributes, SummaryView>[]> {
    this.products = this.planUtilityService.getAllPlansAsMap();
    return this.products;
  }

  private _getPlansResetFilters() {
    this.setProductsPlanMap();
    /** Filters will load while awaiting plans to load, so, wait for those */
    if (this.products?.size > 0) {
      this._populateFilterables();
    }
  }

  private _populateFilterables() {
    const uniqArray = ((array: any[]) => array.filter((item, index, inputArray) => inputArray.indexOf(item) === index));

    /** Sort alphabetically, and populate values for use in template */
    const alphaNumericSort = ((array: any[]) => {
      return array
      .sort((a, b) => {
        if (a < b) { return -1; }
        else if (a > b) { return -1; }
        else { return 0; }
      })
      .map(a => {
        return { label: a, value: a, defaultChecked: true };
      });
    });

    /** Sort by metal level, and populate values for use in template */
    const metalLevelSort = ((array: any[]) => {
      /**
       * 1: rank
       * 2: sort
       * 3: clean
       */
      return array
        .map(metalLevel => {
          switch (metalLevel.toUpperCase()) {
            case 'PLATINUM': return { metalLevel: 'PLATINUM', rank: 1, defaultChecked: true };
            case 'GOLD': return { metalLevel: 'GOLD', rank: 2, defaultChecked: true };
            case 'SILVER': return { metalLevel: 'SILVER', rank: 3, defaultChecked: true };
            case 'BRONZE': return { metalLevel: 'BRONZE', rank: 4, defaultChecked: true };
            case 'CATASTROPHIC': return { metalLevel: 'CATASTROPHIC', rank: 5, defaultChecked: false };
          }
        })
        .sort((
          a: { metalLevel: string, rank: number },
          b: { metalLevel: string, rank: number }
        ) => {
          if (a.rank < b.rank) {
            return -1;
          } else if (a.rank > b.rank) {
            return 1;
          } else {
            return 0;
          }
        })
        .map((val: { metalLevel: string, rank: number, defaultChecked: boolean }) => {
          return { label: val.metalLevel, value: val.metalLevel, defaultChecked: val.defaultChecked };
        });
    });

    const activeProductGroup
      = (this.reducerUtilityService.getComponentStates()['Plans'] as PlansStateStructure).activeProductGroup;

    /** Only active product group plans.. */
    const activePlans: Product<PlanAttributes, SummaryView>[] = this.products.get(activeProductGroup);

    const mapIter: IterableIterator<string> = this.filterContent.keys();
    let result = mapIter.next();

    /**
     * This section helps to handle some of the asynchronicity of multiple filters within the plan-summary list
     * @Todo move some of this stuff so that it truly only runs once, a planFilterService might be good...
     */
    const activeFilters: ActiveFilterMap
      = ( this.reducerUtilityService.getState(PLAN_FILTER_REDUCER) as PlanFilterStateStructure ).activeFilters;
    const filtersActiveForKey: boolean = (() => {
      let returnable = false;
      Object.keys(activeFilters).forEach((key) => {
        if (activeFilters[key].length > 0) {
          returnable = true;
        }
      });
      return returnable;
    })();

    while (!result.done) {

      const key = result.value;

      const parsedContent = JSON.parse(this.filterContent.get(key));

      /** These will be the sorted, raw, results of the filterable listed */
      let sortedResults: PlanFilterItem[] = ((products, attrObj, catalogueAttr) => {
        if (!products?.length) {
          return [];
        }

        switch (attrObj.sortingAlgorithm) {
          case SORTING_ALGORITHMS.ALPHA_NUMERIC:
            return alphaNumericSort(uniqArray(products.map(product => product.attributes[catalogueAttr])));
          case SORTING_ALGORITHMS.METAL_LEVEL:
            return metalLevelSort(uniqArray(products.map(product => product.attributes[catalogueAttr])));
        }

      })(activePlans, parsedContent, key);

      /** Mark boxes for checked if appropriate (when present on reload / when default otherwise) */
      sortedResults = sortedResults.map((filter) => {

        /** If the box has been checked/saved to state, retrieve it */
        const matchedFilter = activeFilters && activeFilters[key] && activeFilters[key].filter(i => {
          return i.value === filter.value;
        });

        if (matchedFilter && matchedFilter.length > 0) {
          filter = matchedFilter[0];
        } else {
          /** If not interactions on the filters have occurred, and the item is marked as default */
          if (filter.defaultChecked && !filtersActiveForKey) {
            this.toggleStateChange(key, filter);
          }
        }
        return filter;
      });

      /** Populate data in map */
      this.renderableMap.set(key, { filterHeader: parsedContent.filterHeader, values: sortedResults });

      result = mapIter.next();

    }

  }

  /**
   * forceUseFromState indicates that the value shouldn't be changed from what's currently in state
   */
  toggleStateChange(catalogueAttribute: string, changedFilter: PlanFilterItem) {
    this.ngRedux.dispatch(toggleFilterValue(catalogueAttribute, changedFilter));
    this.filtersChanged.emit();
  }

  toggleFilterDisplay() {
    this.showFilters = !this.showFilters;
  }
}
